import { Ban, CheckCheck } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useNodeId, useReactFlow, useStore } from "reactflow";

interface CollapsedNodeViewProps {
  icon: React.ReactNode;
  label: string;
  title?: string;
  description?: string;
  iconSize?: number;
  iconClassName?: string;
  borderColor?: string;
  onToggle?: () => void;
  isExpanded?: boolean;
  executionStatus?: string | null;
  variant?: "default" | "start";
  currentHeight?: number;
  status?: string;
  statusType?: "default" | "success" | "warning" | "error" | "loading";
}

// Single source of truth for timing so nothing fights.
// Everything (width, icon scale, label fade/slide) uses this.
const DURATION_MS = 300;

// Static keyframes — defined ONCE, never re-injected on render.
// Zoom-dependent scale is driven purely via a CSS custom property
// (--inv-zoom) set inline, so this <style> block never needs to change.
const STATIC_STYLES = `
  @keyframes slideFromCenter {
    0%   { opacity: 0; transform: translateX(-12px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  @keyframes cardAppear {
    0%   { opacity: 0; transform: translateX(-50%) scale(calc(var(--inv-zoom, 1) * 0.85)); }
    100% { opacity: 1; transform: translateX(-50%) scale(var(--inv-zoom, 1)); }
  }
  @keyframes pulse-soft {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
`;

const CollapsedNodeView: React.FC<CollapsedNodeViewProps> = ({
  icon,
  label,
  title,
  description,
  iconSize = 35,
  iconClassName = "text-primary",
  borderColor,
  onToggle,
  isExpanded = false,
  executionStatus,
  variant = "default",
  currentHeight,
  status,
  statusType = "default",
}) => {
  const isStartNode = variant === "start" || label?.toLowerCase() === "start";

  const [showHoverCard, setShowHoverCard] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasExpandedRef = useRef<boolean>(isExpanded);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reactFlow = useReactFlow();

  const zoom = useStore((s) => s.transform[2]);
  const minZoom = useStore((s) => s.minZoom);
  const maxZoom = useStore((s) => s.maxZoom);

  // FIX: only track *this* node's dragging state instead of scanning
  // every node on every store tick (was O(N) per node = O(N^2) overall).
  const nodeId = useNodeId();
  const isDragging = useStore(
    useCallback(
      (s) =>
        nodeId ? (s.nodeInternals.get(nodeId)?.dragging ?? false) : false,
      [nodeId],
    ),
  );

  const handleMouseEnter = () => {
    if (!isExpanded) {
      const timeout = setTimeout(() => setShowHoverCard(true), 300);
      hoverTimeoutRef.current = timeout;
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowHoverCard(false);
  };

  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const getStatusColor = () => {
    switch (statusType) {
      case "success":
        return "text-networkGreen";
      case "warning":
        return "text-amber-500";
      case "error":
        return "text-destructive";
      case "loading":
        return "text-primaryBlue";
      default:
        return "text-muted-foreground";
    }
  };

  const clampZoom = useCallback(
    (value: number) => Math.max(minZoom ?? 0.1, Math.min(maxZoom ?? 2, value)),
    [minZoom, maxZoom],
  );

  // FIX: instead of a double-rAF + a magic 750ms timeout (~2.5x the real
  // 300ms transition), listen for the actual transitionend on the element
  // that's animating, with a small safety-net timeout as fallback.
  const centerExpandedNode = useCallback(() => {
    const runCenter = () => {
      const nodeEl = containerRef.current?.closest(
        ".react-flow__node",
      ) as HTMLElement | null;
      if (!nodeEl) return;

      const nodeId = nodeEl.getAttribute("data-id");
      const flowNode = nodeId ? reactFlow.getNode(nodeId) : null;

      const reactFlowRoot = nodeEl.closest(".react-flow") as HTMLElement | null;
      const viewportWidth = reactFlowRoot?.clientWidth ?? window.innerWidth;
      const viewportHeight = reactFlowRoot?.clientHeight ?? window.innerHeight;
      const viewportPadding = 32;

      const measuredWidth =
        flowNode?.width || nodeEl.getBoundingClientRect().width;
      const measuredHeight =
        flowNode?.height || nodeEl.getBoundingClientRect().height;

      const fitZoomX =
        (viewportWidth - viewportPadding * 2) / Math.max(measuredWidth, 1);
      const fitZoomY =
        (viewportHeight - viewportPadding * 2) / Math.max(measuredHeight, 1);
      const fitZoom = clampZoom(Math.min(fitZoomX, fitZoomY));

      // Keep user's current zoom unless it would clip the expanded node.
      const targetZoom = clampZoom(Math.min(zoom, fitZoom));

      // Prefer native flow coordinates for deterministic centering.
      if (flowNode?.positionAbsolute) {
        const width = flowNode.width || nodeEl.getBoundingClientRect().width;
        const height = flowNode.height || nodeEl.getBoundingClientRect().height;
        const centerX = flowNode.positionAbsolute.x + width / 2;
        const centerY = flowNode.positionAbsolute.y + height / 2;

        reactFlow.setCenter(centerX, centerY, {
          zoom: targetZoom,
          duration: 400,
        });
        return;
      }

      // Fallback: compute from screen position if node data isn't available yet.
      const nodeRect = nodeEl.getBoundingClientRect();

      const flowCenter = reactFlow.screenToFlowPosition({
        x: nodeRect.left + nodeRect.width / 2,
        y: nodeRect.top + nodeRect.height / 2,
      });

      reactFlow.setCenter(flowCenter.x, flowCenter.y, {
        zoom: targetZoom,
        duration: DURATION_MS,
      });
    };

    const el = containerRef.current;
    if (!el) {
      setTimeout(runCenter, DURATION_MS);
      return;
    }

    let done = false;
    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== "width" || done) return;
      done = true;
      el.removeEventListener("transitionend", handleTransitionEnd);
      runCenter();
    };
    el.addEventListener("transitionend", handleTransitionEnd);

    // Safety net in case transitionend never fires (e.g. reduced motion).
    setTimeout(() => {
      if (done) return;
      done = true;
      el.removeEventListener("transitionend", handleTransitionEnd);
      runCenter();
    }, DURATION_MS + 100);
  }, [reactFlow, zoom, clampZoom]);

  React.useEffect(() => {
    const wasExpanded = wasExpandedRef.current;
    wasExpandedRef.current = isExpanded;

    if (!wasExpanded && isExpanded) {
      centerExpandedNode();
    }
  }, [isExpanded, centerExpandedNode]);

  return (
    <>
      <style>{STATIC_STYLES}</style>

      <div
        ref={containerRef}
        className={`
            font-semibold flex-1 flex items-center
            transition-[width] duration-300 ease-out
            ${
              isExpanded
                ? "w-[320px] flex-row gap-2 justify-start"
                : "w-[135px] flex-col justify-center items-center py-3 px-8"
            }
        `}
        style={{
          height:
            !isExpanded && currentHeight ? `${currentHeight}px` : undefined,
          // FIX: scope layout recalculation to just this element instead
          // of letting the width change ripple out to ancestors/siblings
          // on every animation frame — this is what was causing the
          // resize itself to stutter.
          contain: "layout style",
          willChange: "width",
        }}
        onDoubleClick={(e) => {
          if (!onToggle || isDragging) return;
          e.stopPropagation();
          onToggle();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={onToggle ? "" : undefined}
      >
        {/* Icon — scale transition now matches the container's 300ms */}
        <div
          className={`${borderColor ? "" : iconClassName} ease-out ${
            onToggle ? "cursor-pointer" : ""
          } ${statusType === "loading" ? "animate-pulse" : ""}`}
          style={{
            transform: isExpanded ? "scale(0.7)" : "scale(1)",
            marginBottom: isExpanded ? "0" : "4px",
            color: borderColor || undefined,
            transition: `transform ${DURATION_MS}ms ease-out, opacity ${DURATION_MS}ms ease-out`,
          }}
        >
          {React.cloneElement(icon as React.ReactElement, { size: iconSize })}
        </div>

        {/* Labels */}
        {isExpanded ? (
          <div className="flex flex-col flex-1 min-w-0">
            <div
              className="text-sm font-semibold text-foreground truncate w-fit"
              style={{ color: borderColor }}
            >
              {label}
            </div>
            {(title || status) && (
              <div className="flex items-center gap-2 overflow-hidden">
                {title && title !== label && (
                  <div
                    className={`text-xs text-muted-foreground truncate [text-shadow:2px_2px_5px_rgba(0,0,0,0.1)] max-w-fit`}
                  >
                    {title.length > 20 ? title.substring(0, 18) + "..." : title}
                  </div>
                )}
                {status && (
                  <span
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted/50 ${getStatusColor()} ${statusType === "loading" ? "animate-pulse" : ""}`}
                  >
                    {status}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`flex flex-col items-center justify-start w-full ${!isStartNode ? "h-12" : "h-full"}`}
          >
            <span
              className={`text-[11px] transition-all duration-300 cursor-pointer whitespace-nowrap [text-shadow:2px_2px_5px_rgba(0,0,0,0.1)] text-center block
                 ${statusType == "loading" ? "!animate-pulse" : ""}  `}
              style={{ color: borderColor }}
            >
              {label}
            </span>
            {title && title !== label && (
              <div className="transition-all duration-300 max-w-full">
                <span
                  className={`text-[10px] font-normal text-muted-foreground truncate [text-shadow:2px_2px_5px_rgba(0,0,0,0.1)] w-full text-center block tracking-tight`}
                >
                  {title.length > 15 ? title.substring(0, 13) + "..." : title}
                </span>
              </div>
            )}
            {status && (
              <div className="mt-0.5">
                <span
                  className={`text-[9px] text-muted-foreground font-bold tracking-tighter ${getStatusColor()} ${statusType === "loading" ? "animate-pulse" : ""}`}
                >
                  {status}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Execution status icon */}
        {!isStartNode && !isExpanded && executionStatus && (
          <div
            className="absolute flex items-center justify-end"
            style={{
              bottom: "6px",
              right: "6px",
            }}
          >
            {executionStatus === "success" ||
            executionStatus === "completed" ? (
              <CheckCheck className="text-networkGreen" size={20} />
            ) : executionStatus === "error" ||
              executionStatus === "failed" ||
              executionStatus === "failure" ? (
              <Ban className="text-destructive rotate-90" size={18} />
            ) : null}
          </div>
        )}

        {/* Hover Card */}
        {!isExpanded && showHoverCard && !isDragging && (
          <div
            className="absolute bottom-full left-1/2 mb-2 w-64 bg-background border rounded-2xl shadow-2xl z-50"
            style={{
              // FIX: zoom is now passed as a CSS custom property instead
              // of being baked into the keyframes text on every render.
              ["--inv-zoom" as any]: 1 / zoom,
              marginBottom: `${(isStartNode ? 18 : 8) / zoom}px`,
              pointerEvents: "none",
              borderColor: borderColor,
              boxShadow: `0 8px 24px ${borderColor}33`,
              transformOrigin: "bottom center",
              animation: "cardAppear 0.25s ease-out forwards",
            }}
          >
            <div className="p-3 flex flex-col gap-3">
              {/* TOP ROW — icon left + title/subtitle right + status top-right */}
              <div className="flex items-start gap-3 relative">
                <div
                  className={`shrink-0 ${borderColor ? "" : iconClassName}`}
                  style={{ color: borderColor || undefined }}
                >
                  {React.cloneElement(icon as React.ReactElement, { size: 28 })}
                </div>
                <div className="flex flex-col min-w-0 pr-6">
                  <div className="flex items-center gap-2">
                    <h4
                      className={`text-sm font-bold leading-tight truncate [text-shadow:2px_2px_5px_rgba(0,0,0,0.1)]`}
                      style={{ color: borderColor }}
                    >
                      {label}
                    </h4>
                    {status && (
                      <span
                        className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-muted/50 ${getStatusColor()}`}
                      >
                        {status}
                      </span>
                    )}
                  </div>
                  {title && !isStartNode && (
                    <p className="text-xs text-muted-foreground tracking-wider capitalize mt-0.5 [text-shadow:2px_2px_5px_rgba(0,0,0,0.1)] break-words text-left">
                      {title}
                    </p>
                  )}
                </div>

                {/* Status — top right corner */}
                {!isExpanded && executionStatus && (
                  <div className="absolute top-1/2 right-0 -translate-y-1/2 flex items-center gap-1.5">
                    {executionStatus === "success" ||
                    executionStatus === "completed" ? (
                      <CheckCheck className="text-networkGreen" size={13} />
                    ) : executionStatus === "error" ||
                      executionStatus === "failed" ||
                      executionStatus === "failure" ? (
                      <Ban className="text-destructive" size={13} />
                    ) : executionStatus === "running" ||
                      executionStatus === "pending" ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primaryBlue" />
                    ) : (
                      <span className="text-xs capitalize">
                        {executionStatus}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Description */}
              {description && (
                <div className="border-t border-border pt-2 w-full">
                  <p className="text-[10px] font-semibold text-muted-foreground mb-1 text-left">
                    Description
                  </p>
                  <p className="text-[9px] text-muted-foreground text-left">
                    {description}
                  </p>
                </div>
              )}

              {/* Double-click hint */}
              <div className="border-t border-border pt-2">
                <p className="text-[9px] text-muted-foreground italic text-center">
                  Double-click to expand
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CollapsedNodeView;
